/**
 * Which fields each filter type may be applied to.
 *
 * Extracted from the Vue application's registry rather than transcribed: every
 * filter was probed against every field type, and against a formula, rollup,
 * count and lookup for each formula result type. All four of those holders
 * agreed on every filter, so they collapse to one `formulaTypes` list, and the
 * 91 filters collapse to 18 distinct signatures.
 *
 * A field compatible with no filter at all is disabled in the filter UI, so
 * these lists are also what decides whether a column can be filtered at all.
 */
import type { FieldTypeName, ViewFilterTypeName } from '@/api/types'

export interface FilterCompatibility {
  /** Plain field types the filter applies to. */
  readonly fieldTypes: readonly FieldTypeName[]
  /**
   * Formula result types it applies to.
   *
   * Matched against a formula, rollup, count or lookup field's `formula_type`,
   * which is how a formula returning text becomes text-filterable.
   */
  readonly formulaTypes: readonly string[]
}

const COMPAT_EQUAL: FilterCompatibility = {
  fieldTypes: [
    'autonumber',
    'duration',
    'email',
    'long_text',
    'number',
    'phone_number',
    'rating',
    'text',
    'url',
    'uuid',
  ],
  formulaTypes: ['char', 'duration', 'number', 'text', 'url'],
}

const COMPAT_DATE_IS: FilterCompatibility = {
  fieldTypes: ['created_on', 'date', 'last_modified'],
  formulaTypes: ['date'],
}

const COMPAT_DATE_EQUALS_DAY_OF_MONTH: FilterCompatibility = {
  fieldTypes: ['created_on', 'date', 'last_modified'],
  formulaTypes: [],
}

const COMPAT_HAS_EMPTY_VALUE: FilterCompatibility = {
  fieldTypes: [],
  formulaTypes: [
    'array',
    'boolean',
    'button',
    'char',
    'date',
    'date_interval',
    'duration',
    'link',
    'multiple_select',
    'number',
    'single_file',
    'single_select',
    'text',
    'url',
    'uuid',
  ],
}

const COMPAT_HAS_ALL_VALUES_EQUAL: FilterCompatibility = {
  fieldTypes: [],
  formulaTypes: [],
}

const COMPAT_CONTAINS: FilterCompatibility = {
  fieldTypes: [
    'autonumber',
    'created_on',
    'date',
    'email',
    'last_modified',
    'long_text',
    'multiple_select',
    'number',
    'phone_number',
    'single_select',
    'text',
    'url',
  ],
  formulaTypes: [
    'char',
    'date',
    'multiple_select',
    'number',
    'single_select',
    'text',
    'url',
  ],
}

const COMPAT_CONTAINS_WORD: FilterCompatibility = {
  fieldTypes: ['email', 'long_text', 'multiple_select', 'single_select', 'text', 'url'],
  formulaTypes: ['char', 'multiple_select', 'single_select', 'text', 'url'],
}

const COMPAT_FILENAME_CONTAINS: FilterCompatibility = {
  fieldTypes: ['file'],
  formulaTypes: [],
}

const COMPAT_LENGTH_IS_LOWER_THAN: FilterCompatibility = {
  fieldTypes: ['email', 'long_text', 'phone_number', 'text', 'url'],
  formulaTypes: ['url'],
}

const COMPAT_HIGHER_THAN: FilterCompatibility = {
  fieldTypes: ['autonumber', 'duration', 'number', 'rating'],
  formulaTypes: ['duration', 'number'],
}

const COMPAT_IS_EVEN_AND_WHOLE: FilterCompatibility = {
  fieldTypes: ['autonumber', 'number'],
  formulaTypes: ['number'],
}

const COMPAT_SINGLE_SELECT_EQUAL: FilterCompatibility = {
  fieldTypes: ['single_select'],
  formulaTypes: ['single_select'],
}

const COMPAT_BOOLEAN: FilterCompatibility = {
  fieldTypes: ['boolean'],
  formulaTypes: ['boolean'],
}

const COMPAT_LINK_ROW_HAS: FilterCompatibility = {
  fieldTypes: ['link_row'],
  formulaTypes: [],
}

const COMPAT_MULTIPLE_SELECT_HAS: FilterCompatibility = {
  fieldTypes: ['multiple_select'],
  formulaTypes: ['multiple_select'],
}

const COMPAT_MULTIPLE_COLLABORATORS_HAS: FilterCompatibility = {
  fieldTypes: ['multiple_collaborators'],
  formulaTypes: [],
}

const COMPAT_EMPTY: FilterCompatibility = {
  fieldTypes: [
    'boolean',
    'created_on',
    'date',
    'duration',
    'email',
    'file',
    'last_modified',
    'link_row',
    'long_text',
    'multiple_collaborators',
    'multiple_select',
    'number',
    'password',
    'phone_number',
    'single_select',
    'text',
    'url',
  ],
  formulaTypes: [
    'array',
    'boolean',
    'button',
    'char',
    'date',
    'date_interval',
    'duration',
    'link',
    'multiple_select',
    'number',
    'single_file',
    'single_select',
    'text',
    'url',
    'uuid',
  ],
}

const COMPAT_USER_IS: FilterCompatibility = {
  fieldTypes: ['created_by', 'last_modified_by'],
  formulaTypes: [],
}

/** Every filter type, and the fields it applies to. */
export const FILTER_COMPATIBILITY = {
  equal: COMPAT_EQUAL,
  not_equal: COMPAT_EQUAL,
  date_is: COMPAT_DATE_IS,
  date_is_not: COMPAT_DATE_IS,
  date_is_before: COMPAT_DATE_IS,
  date_is_on_or_before: COMPAT_DATE_IS,
  date_is_after: COMPAT_DATE_IS,
  date_is_on_or_after: COMPAT_DATE_IS,
  date_is_within: COMPAT_DATE_IS,
  date_equal: COMPAT_DATE_IS,
  date_not_equal: COMPAT_DATE_IS,
  date_equals_today: COMPAT_DATE_IS,
  date_before_today: COMPAT_DATE_IS,
  date_after_today: COMPAT_DATE_IS,
  date_within_days: COMPAT_DATE_IS,
  date_within_weeks: COMPAT_DATE_IS,
  date_within_months: COMPAT_DATE_IS,
  date_equals_days_ago: COMPAT_DATE_IS,
  date_equals_months_ago: COMPAT_DATE_IS,
  date_equals_years_ago: COMPAT_DATE_IS,
  date_equals_week: COMPAT_DATE_IS,
  date_equals_month: COMPAT_DATE_IS,
  date_equals_year: COMPAT_DATE_IS,
  date_equals_day_of_month: COMPAT_DATE_EQUALS_DAY_OF_MONTH,
  date_before: COMPAT_DATE_IS,
  date_before_or_equal: COMPAT_DATE_IS,
  date_after: COMPAT_DATE_IS,
  date_after_or_equal: COMPAT_DATE_IS,
  date_after_days_ago: COMPAT_DATE_IS,
  has_empty_value: COMPAT_HAS_EMPTY_VALUE,
  has_not_empty_value: COMPAT_HAS_EMPTY_VALUE,
  has_value_equal: COMPAT_HAS_EMPTY_VALUE,
  has_not_value_equal: COMPAT_HAS_EMPTY_VALUE,
  has_value_contains: COMPAT_HAS_EMPTY_VALUE,
  has_not_value_contains: COMPAT_HAS_EMPTY_VALUE,
  has_value_contains_word: COMPAT_HAS_EMPTY_VALUE,
  has_not_value_contains_word: COMPAT_HAS_EMPTY_VALUE,
  has_value_length_is_lower_than: COMPAT_HAS_EMPTY_VALUE,
  has_all_values_equal: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_any_select_option_equal: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_none_select_option_equal: COMPAT_HAS_ALL_VALUES_EQUAL,
  contains: COMPAT_CONTAINS,
  contains_not: COMPAT_CONTAINS,
  contains_word: COMPAT_CONTAINS_WORD,
  doesnt_contain_word: COMPAT_CONTAINS_WORD,
  filename_contains: COMPAT_FILENAME_CONTAINS,
  has_file_type: COMPAT_FILENAME_CONTAINS,
  files_lower_than: COMPAT_FILENAME_CONTAINS,
  length_is_lower_than: COMPAT_LENGTH_IS_LOWER_THAN,
  higher_than: COMPAT_HIGHER_THAN,
  higher_than_or_equal: COMPAT_HIGHER_THAN,
  lower_than: COMPAT_HIGHER_THAN,
  lower_than_or_equal: COMPAT_HIGHER_THAN,
  is_even_and_whole: COMPAT_IS_EVEN_AND_WHOLE,
  single_select_equal: COMPAT_SINGLE_SELECT_EQUAL,
  single_select_not_equal: COMPAT_SINGLE_SELECT_EQUAL,
  single_select_is_any_of: COMPAT_SINGLE_SELECT_EQUAL,
  single_select_is_none_of: COMPAT_SINGLE_SELECT_EQUAL,
  boolean: COMPAT_BOOLEAN,
  link_row_has: COMPAT_LINK_ROW_HAS,
  link_row_has_not: COMPAT_LINK_ROW_HAS,
  link_row_contains: COMPAT_LINK_ROW_HAS,
  link_row_not_contains: COMPAT_LINK_ROW_HAS,
  multiple_select_has: COMPAT_MULTIPLE_SELECT_HAS,
  multiple_select_has_not: COMPAT_MULTIPLE_SELECT_HAS,
  multiple_collaborators_has: COMPAT_MULTIPLE_COLLABORATORS_HAS,
  multiple_collaborators_has_not: COMPAT_MULTIPLE_COLLABORATORS_HAS,
  empty: COMPAT_EMPTY,
  not_empty: COMPAT_EMPTY,
  user_is: COMPAT_USER_IS,
  user_is_not: COMPAT_USER_IS,
  has_value_higher: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_not_value_higher: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_value_higher_or_equal: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_not_value_higher_or_equal: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_value_lower: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_not_value_lower: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_value_lower_or_equal: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_not_value_lower_or_equal: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_date_equal: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_not_date_equal: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_date_before: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_not_date_before: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_date_on_or_before: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_not_date_on_or_before: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_date_after: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_not_date_after: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_date_on_or_after: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_not_date_on_or_after: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_date_within: COMPAT_HAS_ALL_VALUES_EQUAL,
  has_not_date_within: COMPAT_HAS_ALL_VALUES_EQUAL,
} satisfies Record<ViewFilterTypeName, FilterCompatibility>

/**
 * Filters hidden from the filter-type dropdown but still honoured when stored.
 *
 * All nineteen are the single-step date filters, superseded by the multi-step
 * ones. Views that already use them keep working; the type just cannot be
 * chosen again.
 */
export const DEPRECATED_FILTERS: ReadonlySet<ViewFilterTypeName> = new Set([
  'date_equal',
  'date_not_equal',
  'date_equals_today',
  'date_before_today',
  'date_after_today',
  'date_within_days',
  'date_within_weeks',
  'date_within_months',
  'date_equals_days_ago',
  'date_equals_months_ago',
  'date_equals_years_ago',
  'date_equals_week',
  'date_equals_month',
  'date_equals_year',
  'date_before',
  'date_before_or_equal',
  'date_after',
  'date_after_or_equal',
  'date_after_days_ago',
])

/**
 * Filters that may not be used on a publicly shared view.
 *
 * Both compare against a user id, which would let whoever holds a public link
 * probe for which people exist in the workspace.
 */
export const PRIVATE_ONLY_FILTERS: ReadonlySet<ViewFilterTypeName> = new Set([
  'user_is',
  'user_is_not',
])

/** Filters that render no input, and whose stored value is always empty. */
export const VALUELESS_FILTERS: ReadonlySet<ViewFilterTypeName> = new Set([
  'date_equals_today',
  'date_before_today',
  'date_after_today',
  'date_equals_year',
  'is_even_and_whole',
  'empty',
  'not_empty',
])
